import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { faCalendarCircleMinus } from '@fortawesome/pro-regular-svg-icons/faCalendarCircleMinus';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Col, type TableColumnsType, Typography } from 'antd';
import { PosCard } from 'pos-client/components/shared/Display/Card/PosCard.tsx';
import { PosTable } from 'pos-client/components/shared/Display/Table/PosTable.tsx';
import { useTableData } from 'pos-client/hooks/useTableData.ts';
import { CreateEditNonTradingDatesModal } from 'pos-client/pages/locations/Modals/CreateEditNonTradingDatesModal.tsx';
import { convertISOStringToLocalDate } from 'pos-client/utils/dateHelpers.ts';
import { trpc } from 'pos-client/utils/trpc.ts';
import type { NonTradingDatesModel } from 'pos-shared/models/openingHoursGroupSchema.ts';

export const NonTradingDates = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { data, isLoading } = trpc.getAllNonTradingDates.useQuery({
    id: id || '',
  });
  const tableData = useTableData(
    data,
    (x: NonTradingDatesModel) => x.id || x.date
  );
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalData, setModalData] = useState<NonTradingDatesModel>();

  const { Text } = Typography;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: TableColumnsType<any> = [
    {
      title: t('common.date.date'),
      dataIndex: 'date',
      key: 'date',
      render: (_, record) => (
        <Text>{convertISOStringToLocalDate(record.date)}</Text>
      ),
    },
    {
      title: '',
      dataIndex: 'id',
      width: 10,
      render: (_, record) => (
        <Button size="small" onClick={() => showEditModal(record)}>
          {t('common.buttons.edit')}
        </Button>
      ),
    },
  ];

  const showModal = () => {
    setIsModalVisible(true);
  };

  const showEditModal = (data: NonTradingDatesModel) => {
    setModalData(data);
    setIsModalVisible(true);
  };

  const handleOk = () => {
    setIsModalVisible(false);
    setModalData(undefined);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setModalData(undefined);
  };

  return (
    <Col>
      <PosCard
        headerProps={{
          title: t('locations.trading.nonTradingDates'),
          icon: faCalendarCircleMinus,
          action: (
            <Button
              onClick={showModal}
              icon={<FontAwesomeIcon icon={faPlus} />}
            >
              {t('common.buttons.addNew')}
            </Button>
          ),
        }}
      >
        <PosTable
          id="nonTradingDatesDetailsTable"
          columns={columns}
          dataSource={tableData}
          isLoading={isLoading}
          fePagination
        />
        <CreateEditNonTradingDatesModal
          handleOk={handleOk}
          handleCancel={handleCancel}
          isVisible={isModalVisible}
          data={modalData}
        />
      </PosCard>
    </Col>
  );
};
